import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Switch,
  Input,
  IconButton,
  FormControl,
  FormLabel,
  useToast,
  Avatar,
  AvatarGroup,
  Divider,
  Collapse,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiCalendar, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { doc, onSnapshot, setDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';

async function logWeekendCoverageAudit({ action, currentUser, details }) {
  try {
    await addDoc(collection(db, 'auditLogs'), {
      action,
      actionLabel: {
        weekend_coverage_toggled: 'Weekend Coverage Toggled',
        weekend_coverage_override_added: 'State Override Added',
        weekend_coverage_override_removed: 'State Override Removed',
        weekend_coverage_weekends_generated: 'Weekends Generated',
        weekend_coverage_weekend_added: 'Custom Weekend Added',
        weekend_coverage_weekend_updated: 'Weekend Updated',
        weekend_coverage_weekend_deleted: 'Weekend Deleted',
      }[action] || action,
      userId: currentUser?.uid || 'unknown',
      userName: currentUser?.displayName || currentUser?.Name || currentUser?.email || 'Unknown User',
      details,
      entityType: 'weekendCoverage',
      entityId: 'config',
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error logging weekend coverage audit:', error);
  }
}

function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateDisplay(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function generateUpcomingWeekends(existingWeekends = [], count = 8) {
  const now = new Date();
  const today = formatDateLocal(now);
  const day = now.getDay(); // 0=Sun … 6=Sat
  const daysUntilSaturday = day === 6 ? 0 : ((6 - day + 7) % 7);

  const existingStartDates = new Set(existingWeekends.map(w => w.startDate));
  const weekends = [];

  for (let i = 0; weekends.length < count && i < count + 20; i++) {
    const saturday = new Date(now);
    saturday.setDate(now.getDate() + daysUntilSaturday + i * 7);
    const startDate = formatDateLocal(saturday);

    if (startDate < today) continue;
    if (existingStartDates.has(startDate)) continue;

    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() + 1);

    weekends.push({
      id: crypto.randomUUID(),
      startDate,
      endDate: formatDateLocal(sunday),
      assignees: [],
      lastAssignedIndex: -1,
      label: '',
    });
  }

  return weekends;
}

const DEFAULT_CONFIG = {
  enabled: false,
  timezone: 'America/New_York',
  stateOverrides: {},
  weekends: [],
};

export default function WeekendCoverageManager({ crmUsers }) {
  const { currentUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const [newOverrideState, setNewOverrideState] = useState('');
  const [newOverrideUser, setNewOverrideUser] = useState(null);

  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customLabel, setCustomLabel] = useState('');

  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const docRef = doc(db, 'weekendCoverage', 'config');
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      setConfig(snapshot.exists() ? snapshot.data() : { ...DEFAULT_CONFIG });
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const userOptions = useMemo(
    () =>
      crmUsers.map((user) => ({
        label: user.name || user.Name || user.email,
        value: user.uid,
        photoURL: user.photoURL,
      })),
    [crmUsers],
  );

  const getUserDetails = (uid) => crmUsers.find((u) => u.uid === uid) || {};
  const getUserName = (uid) => {
    const u = getUserDetails(uid);
    return u.name || u.Name || u.email || uid;
  };

  const saveConfig = async (newConfig, auditAction = null, auditDetails = {}) => {
    setSaving(true);
    try {
      const docRef = doc(db, 'weekendCoverage', 'config');
      const isNew = !config?.createdAt;
      await setDoc(docRef, {
        ...newConfig,
        updatedAt: serverTimestamp(),
        ...(isNew ? { createdAt: serverTimestamp() } : {}),
      });

      if (auditAction) {
        await logWeekendCoverageAudit({ action: auditAction, currentUser, details: auditDetails });
      }
      return true;
    } catch (error) {
      console.error(error);
      toast({ title: 'Error saving weekend coverage', description: error.message, status: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle ──────────────────────────────────────────────
  const handleToggleEnabled = async () => {
    const newEnabled = !config.enabled;
    const ok = await saveConfig(
      { ...config, enabled: newEnabled },
      'weekend_coverage_toggled',
      { enabled: newEnabled },
    );
    if (ok) {
      toast({
        title: newEnabled ? 'Weekend coverage enabled' : 'Weekend coverage disabled',
        status: newEnabled ? 'success' : 'info',
        duration: 2000,
      });
    }
  };

  // ── State Overrides ─────────────────────────────────────
  const handleAddOverride = async () => {
    const state = newOverrideState.toUpperCase().trim();
    if (!state || state.length !== 2) {
      toast({ title: 'Enter a valid 2-letter state code', status: 'error' });
      return;
    }
    if (!newOverrideUser) {
      toast({ title: 'Select a user for this override', status: 'error' });
      return;
    }
    const newOverrides = { ...(config.stateOverrides || {}), [state]: newOverrideUser.value };
    const ok = await saveConfig(
      { ...config, stateOverrides: newOverrides },
      'weekend_coverage_override_added',
      { state, userId: newOverrideUser.value, userName: newOverrideUser.label },
    );
    if (ok) {
      setNewOverrideState('');
      setNewOverrideUser(null);
      toast({ title: `Override added: ${state} → ${newOverrideUser.label}`, status: 'success', duration: 2000 });
    }
  };

  const handleRemoveOverride = async (state) => {
    const userId = (config.stateOverrides || {})[state];
    const newOverrides = { ...(config.stateOverrides || {}) };
    delete newOverrides[state];
    const ok = await saveConfig(
      { ...config, stateOverrides: newOverrides },
      'weekend_coverage_override_removed',
      { state, userId, userName: getUserName(userId) },
    );
    if (ok) {
      toast({ title: `Override removed: ${state}`, status: 'success', duration: 2000 });
    }
  };

  // ── Weekend Schedule ────────────────────────────────────
  const handleGenerateWeekends = async () => {
    const newWeekends = generateUpcomingWeekends(config.weekends || [], 8);
    if (newWeekends.length === 0) {
      toast({ title: 'No new weekends to generate', description: 'Next 8 weekends are already in the schedule', status: 'info' });
      return;
    }
    const allWeekends = [...(config.weekends || []), ...newWeekends].sort((a, b) => a.startDate.localeCompare(b.startDate));
    const ok = await saveConfig(
      { ...config, weekends: allWeekends },
      'weekend_coverage_weekends_generated',
      { generated: newWeekends.length, totalAfter: allWeekends.length },
    );
    if (ok) {
      toast({ title: `Generated ${newWeekends.length} weekends`, status: 'success', duration: 2000 });
    }
  };

  const handleAddCustomWeekend = async () => {
    if (!customStart || !customEnd) {
      toast({ title: 'Start and end dates are required', status: 'error' });
      return;
    }
    if (customStart > customEnd) {
      toast({ title: 'Start date must be before end date', status: 'error' });
      return;
    }
    const newWeekend = {
      id: crypto.randomUUID(),
      startDate: customStart,
      endDate: customEnd,
      assignees: [],
      lastAssignedIndex: -1,
      label: customLabel || '',
    };
    const allWeekends = [...(config.weekends || []), newWeekend].sort((a, b) => a.startDate.localeCompare(b.startDate));
    const ok = await saveConfig(
      { ...config, weekends: allWeekends },
      'weekend_coverage_weekend_added',
      { startDate: customStart, endDate: customEnd, label: customLabel },
    );
    if (ok) {
      setCustomStart('');
      setCustomEnd('');
      setCustomLabel('');
      setShowCustomRange(false);
      toast({ title: 'Custom date range added', status: 'success', duration: 2000 });
    }
  };

  const handleUpdateAssignees = async (weekendId, selectedOptions) => {
    const newAssignees = (selectedOptions || []).map((o) => o.value);
    const allWeekends = (config.weekends || []).map((w) =>
      w.id === weekendId ? { ...w, assignees: newAssignees } : w,
    );
    const weekend = allWeekends.find((w) => w.id === weekendId);
    await saveConfig({ ...config, weekends: allWeekends }, 'weekend_coverage_weekend_updated', {
      weekendId,
      startDate: weekend?.startDate,
      assigneeCount: newAssignees.length,
      assignees: newAssignees.map((uid) => getUserName(uid)),
    });
  };

  const handleUpdateLabel = async (weekendId, label) => {
    const current = (config.weekends || []).find((w) => w.id === weekendId);
    if (current && current.label === label) return;
    const allWeekends = (config.weekends || []).map((w) =>
      w.id === weekendId ? { ...w, label } : w,
    );
    await saveConfig({ ...config, weekends: allWeekends }, 'weekend_coverage_weekend_updated', {
      weekendId,
      label,
    });
  };

  const handleDeleteWeekend = async (weekendId) => {
    const weekend = (config.weekends || []).find((w) => w.id === weekendId);
    const allWeekends = (config.weekends || []).filter((w) => w.id !== weekendId);
    const ok = await saveConfig({ ...config, weekends: allWeekends }, 'weekend_coverage_weekend_deleted', {
      weekendId,
      startDate: weekend?.startDate,
      endDate: weekend?.endDate,
    });
    if (ok) {
      toast({ title: 'Weekend removed', status: 'success', duration: 2000 });
    }
  };

  // ── Render ──────────────────────────────────────────────
  if (loading || !config) {
    return (
      <Box p={6} bg="white" borderRadius="lg" shadow="sm">
        <Text color="gray.500">Loading weekend coverage...</Text>
      </Box>
    );
  }

  const today = formatDateLocal(new Date());
  const sortedWeekends = [...(config.weekends || [])].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const futureWeekends = sortedWeekends.filter((w) => w.endDate >= today);
  const pastWeekends = sortedWeekends.filter((w) => w.endDate < today);
  const stateOverrideEntries = Object.entries(config.stateOverrides || {});

  return (
    <Box p={6} bg="white" borderRadius="lg" shadow="sm">
      {/* ── Header ─────────────────────────────────── */}
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <HStack spacing={3}>
            <Text fontSize="lg" fontWeight="bold">Weekend Coverage</Text>
            <Badge colorScheme={config.enabled ? 'green' : 'gray'} fontSize="sm">
              {config.enabled ? 'Active' : 'Inactive'}
            </Badge>
          </HStack>
          <Text fontSize="sm" color="gray.500">
            Override round robin assignments on scheduled weekends
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Text fontSize="sm" color="gray.500">{config.enabled ? 'Enabled' : 'Disabled'}</Text>
          <Switch
            colorScheme="green"
            size="lg"
            isChecked={config.enabled}
            onChange={handleToggleEnabled}
            isDisabled={saving}
          />
        </HStack>
      </HStack>

      {/* ── State Overrides ────────────────────────── */}
      <Box mb={6}>
        <Text fontWeight="semibold" mb={2}>State Overrides</Text>
        <Text fontSize="xs" color="gray.500" mb={3}>
          Leads from these states are always routed to the specified person on weekends, bypassing the rotation.
        </Text>

        {stateOverrideEntries.length > 0 && (
          <VStack align="stretch" spacing={2} mb={3}>
            {stateOverrideEntries.map(([state, userId]) => {
              const user = getUserDetails(userId);
              return (
                <HStack key={state} p={2} bg="gray.50" borderRadius="md" justify="space-between">
                  <HStack spacing={3}>
                    <Badge colorScheme="orange" fontSize="md" px={2} py={1}>{state}</Badge>
                    <HStack spacing={2}>
                      <Avatar size="xs" name={user.name || user.Name} src={user.photoURL} />
                      <Text fontSize="sm">{getUserName(userId)}</Text>
                    </HStack>
                  </HStack>
                  <IconButton
                    icon={<FiTrash2 />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Remove override"
                    onClick={() => handleRemoveOverride(state)}
                    isDisabled={saving}
                  />
                </HStack>
              );
            })}
          </VStack>
        )}

        <HStack spacing={2}>
          <Input
            placeholder="ST"
            value={newOverrideState}
            onChange={(e) => setNewOverrideState(e.target.value.toUpperCase())}
            maxLength={2}
            w="80px"
            size="sm"
          />
          <Box flex={1}>
            <Select
              options={userOptions}
              value={newOverrideUser}
              onChange={setNewOverrideUser}
              placeholder="Select user..."
              size="sm"
              isClearable
            />
          </Box>
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            colorScheme="orange"
            onClick={handleAddOverride}
            isDisabled={saving || !newOverrideState || !newOverrideUser}
          >
            Add
          </Button>
        </HStack>
      </Box>

      <Divider mb={6} />

      {/* ── Weekend Schedule ───────────────────────── */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Text fontWeight="semibold">Weekend Schedule</Text>
          <HStack spacing={2}>
            <Button size="sm" leftIcon={<FiCalendar />} onClick={() => setShowCustomRange(!showCustomRange)} variant="outline">
              Custom Range
            </Button>
            <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={handleGenerateWeekends} isLoading={saving}>
              Generate Next 8 Weekends
            </Button>
          </HStack>
        </HStack>

        {/* Custom date range form */}
        <Collapse in={showCustomRange} animateOpacity>
          <Box p={4} bg="blue.50" borderRadius="md" mb={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>Add Custom Date Range</Text>
            <HStack spacing={2} flexWrap="wrap">
              <FormControl w="auto">
                <FormLabel fontSize="xs">Start Date</FormLabel>
                <Input type="date" size="sm" value={customStart} onChange={(e) => setCustomStart(e.target.value)} bg="white" />
              </FormControl>
              <FormControl w="auto">
                <FormLabel fontSize="xs">End Date</FormLabel>
                <Input type="date" size="sm" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} bg="white" />
              </FormControl>
              <FormControl w="auto">
                <FormLabel fontSize="xs">Label (optional)</FormLabel>
                <Input size="sm" placeholder="e.g. President's Day" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} bg="white" />
              </FormControl>
              <Button size="sm" colorScheme="blue" onClick={handleAddCustomWeekend} isDisabled={saving || !customStart || !customEnd} alignSelf="flex-end">
                Add
              </Button>
            </HStack>
          </Box>
        </Collapse>

        {/* Empty state */}
        {futureWeekends.length === 0 && pastWeekends.length === 0 && (
          <Box textAlign="center" py={8} color="gray.500">
            <Text>No weekends scheduled. Click &quot;Generate Next 8 Weekends&quot; to get started.</Text>
          </Box>
        )}

        {/* Future / current weekends */}
        <VStack align="stretch" spacing={3}>
          {futureWeekends.map((weekend) => {
            const hasAssignees = weekend.assignees?.length > 0;
            const isActive = today >= weekend.startDate && today <= weekend.endDate;
            const assigneeOptions = (weekend.assignees || []).map((uid) => {
              const u = getUserDetails(uid);
              return { label: u.name || u.Name || u.email || uid, value: uid, photoURL: u.photoURL };
            });

            return (
              <Box
                key={weekend.id}
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor={isActive ? 'green.300' : hasAssignees ? 'blue.200' : 'gray.200'}
                bg={isActive ? 'green.50' : 'white'}
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={3} flexWrap="wrap">
                    <Text fontWeight="medium" fontSize="sm">
                      {formatDateDisplay(weekend.startDate)} – {formatDateDisplay(weekend.endDate)}
                    </Text>
                    {weekend.label && <Badge colorScheme="purple" variant="subtle" fontSize="xs">{weekend.label}</Badge>}
                    {isActive && <Badge colorScheme="green" variant="solid" fontSize="xs">Active Now</Badge>}
                    {!isActive && hasAssignees && <Badge colorScheme="blue" variant="subtle" fontSize="xs">Scheduled</Badge>}
                    {!isActive && !hasAssignees && <Badge colorScheme="gray" variant="subtle" fontSize="xs">No Coverage</Badge>}
                  </HStack>
                  <IconButton
                    icon={<FiTrash2 />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Remove weekend"
                    onClick={() => handleDeleteWeekend(weekend.id)}
                    isDisabled={saving}
                  />
                </HStack>

                <HStack spacing={2} align="start">
                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">Rotation Assignees</FormLabel>
                    <Select
                      isMulti
                      options={userOptions}
                      value={assigneeOptions}
                      onChange={(selected) => handleUpdateAssignees(weekend.id, selected)}
                      placeholder="Select weekend coverage..."
                      size="sm"
                      closeMenuOnSelect={false}
                    />
                  </FormControl>
                  <FormControl w="200px" flexShrink={0}>
                    <FormLabel fontSize="xs" color="gray.500">Label</FormLabel>
                    <Input
                      key={`label-${weekend.id}-${weekend.label ?? ''}`}
                      size="sm"
                      placeholder="Optional"
                      defaultValue={weekend.label || ''}
                      onBlur={(e) => handleUpdateLabel(weekend.id, e.target.value)}
                    />
                  </FormControl>
                </HStack>
              </Box>
            );
          })}
        </VStack>

        {/* Past weekends */}
        {pastWeekends.length > 0 && (
          <Box mt={4}>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={showPast ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setShowPast(!showPast)}
              color="gray.500"
            >
              {showPast ? 'Hide' : 'Show'} {pastWeekends.length} past weekend{pastWeekends.length !== 1 ? 's' : ''}
            </Button>
            <Collapse in={showPast} animateOpacity>
              <VStack align="stretch" spacing={2} mt={2} opacity={0.5}>
                {pastWeekends.map((weekend) => (
                  <HStack key={weekend.id} p={3} borderRadius="md" bg="gray.50" justify="space-between">
                    <HStack spacing={3}>
                      <Text fontSize="sm" color="gray.500">
                        {formatDateDisplay(weekend.startDate)} – {formatDateDisplay(weekend.endDate)}
                      </Text>
                      {weekend.label && <Badge colorScheme="gray" variant="subtle" fontSize="xs">{weekend.label}</Badge>}
                      <AvatarGroup size="xs" max={3}>
                        {(weekend.assignees || []).map((uid) => {
                          const u = getUserDetails(uid);
                          return <Avatar key={uid} name={u.name || u.Name} src={u.photoURL} />;
                        })}
                      </AvatarGroup>
                    </HStack>
                    <IconButton
                      icon={<FiTrash2 />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      aria-label="Remove"
                      onClick={() => handleDeleteWeekend(weekend.id)}
                    />
                  </HStack>
                ))}
              </VStack>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
}
